/**
 * Tiny fetch wrapper for the platform admin SPA.
 *
 * - Includes credentials (HttpOnly auth cookies)
 * - Surfaces typed API errors with status + message
 * - JSON-only by convention
 */

export interface ApiErrorBody {
  error?: string;
  message?: string;
  status?: number;
  [k: string]: unknown;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(public readonly status: number, public readonly body: ApiErrorBody) {
    super(body.message ?? body.error ?? `HTTP ${status}`);
  }
}

async function request<TResponse>(
  method: string,
  path: string,
  body?: unknown,
): Promise<TResponse> {
  const url = path.startsWith('/') ? path : `/${path}`;
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const response = await fetch(url, init);
  const text = await response.text();
  const json: unknown = text ? safeParseJson(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, (json ?? { error: 'unknown' }) as ApiErrorBody);
  }
  return json as TResponse;
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ─── Auth-flow specific helpers ──────────────────────────────────────────

export interface LoginResponse {
  challenge_token: string;
  expires_in_seconds: number;
}

export interface VerifyResponse {
  platform_admin_id: string;
  must_change_password: boolean;
  session_ttl_seconds: number;
}

export interface MeResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  must_change_password: boolean;
  last_login_at: string | null;
  step_up_until: number | null;
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/api/v1/auth/login', { email, password }),
  verifyTwoFactor: (code: string, challengeToken: string) =>
    api.post<VerifyResponse>('/api/v1/auth/verify-2fa', {
      code,
      challenge_token: challengeToken,
    }),
  logout: () => api.post<{ ok: boolean }>('/api/v1/auth/logout'),
  me: () => api.get<MeResponse>('/api/v1/auth/me'),
  refresh: () =>
    api.post<{ platform_admin_id: string; session_ttl_seconds: number }>(
      '/api/v1/auth/refresh',
    ),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: boolean; must_relogin: boolean }>('/api/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// ─── Tenants ──────────────────────────────────────────────────────────────

export type LifecycleState =
  | 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED' | 'OFFBOARDED' | 'DELETED';

export type BillingStatus = 'PENDING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';

export interface TenantSummary {
  id: string;
  slug: string;
  display_name: string;
  custom_domain: string | null;
  lifecycle_state: LifecycleState;
  billing_status: BillingStatus;
  timezone: string;
  d1_database_id: string;
  onboarded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TenantFull extends TenantSummary {
  r2_prefix: string;
  operating_hours_json: string | null;
  brand_logo_r2_key: string | null;
  brand_wordmark_r2_key: string | null;
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_accent_color: string | null;
  brand_font_heading: string | null;
  brand_font_body: string | null;
  brand_default_color_mode: 'DARK' | 'LIGHT';
  brand_pdf_letterhead_r2_key: string | null;
  email_from_name: string | null;
  email_from_address: string | null;
  email_reply_to: string | null;
  email_dkim_verified: number;
  email_spf_verified: number;
  email_dmarc_verified: number;
  email_last_verified_at: string | null;
  suspended_at: string | null;
  archived_at: string | null;
  offboarded_at: string | null;
  deleted_at: string | null;
}

export interface CreateTenantArgs {
  slug: string;
  display_name: string;
  timezone: string;
  custom_domain?: string | null;
  operating_hours_json?: string | null;
}

export interface UpdateTenantArgs {
  display_name?: string;
  custom_domain?: string | null;
  timezone?: string;
  operating_hours_json?: string | null;
}

// ─── Brand extraction ────────────────────────────────────────────────────

export interface DecisionPointProposal {
  question: string;
  context?: string;
  ai_recommendation?: string;
  options?: string[];
}

export interface BrandKitProposal {
  primary_color: string;
  secondary_color?: string;
  accent_color?: string;
  font_heading?: string;
  font_body?: string;
  default_color_mode: 'DARK' | 'LIGHT';
  tone_hints?: string;
  logo_description?: string;
  decision_points?: DecisionPointProposal[];
}

export interface BrandExtractionResult {
  proposal: BrandKitProposal;
  source_type: 'URL' | 'PDF';
  source_reference: string;
}

export interface AcceptedBrandKit {
  brand_logo_r2_key?: string | null;
  brand_wordmark_r2_key?: string | null;
  brand_primary_color: string;
  brand_secondary_color?: string | null;
  brand_accent_color?: string | null;
  brand_font_heading?: string | null;
  brand_font_body?: string | null;
  brand_default_color_mode: 'DARK' | 'LIGHT';
  brand_pdf_letterhead_r2_key?: string | null;
}

export const brandExtractionApi = {
  fromUrl: (tenantId: string, url: string) =>
    api.post<BrandExtractionResult>(
      `/api/v1/tenants/${tenantId}/brand-extraction/extract-from-url`,
      { url },
    ),
  fromPdf: async (tenantId: string, file: File): Promise<BrandExtractionResult> => {
    const fd = new FormData();
    fd.append('pdf', file);
    const r = await fetch(`/api/v1/tenants/${tenantId}/brand-extraction/extract-from-pdf`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const text = await r.text();
    const json = text ? JSON.parse(text) : null;
    if (!r.ok) throw new ApiError(r.status, json ?? {});
    return json as BrandExtractionResult;
  },
  accept: (tenantId: string, fields: AcceptedBrandKit) =>
    api.post<{ ok: boolean }>(`/api/v1/tenants/${tenantId}/brand-extraction/accept`, fields),
};

// ─── Offerings extraction ────────────────────────────────────────────────

export interface OfferingProposal {
  type: 'SERVICE' | 'PRODUCT';
  name: string;
  description?: string;
  category?: string;
  duration_minutes?: number;
  location?: string;
  sku?: string;
  unit?: string;
}

export interface OfferingsProposal {
  offerings: OfferingProposal[];
  decision_points?: DecisionPointProposal[];
}

export interface OfferingsExtractionResult {
  proposal: OfferingsProposal;
  source_type: 'URL' | 'PDF' | 'CSV';
  source_reference: string;
}

export const offeringsExtractionApi = {
  fromUrl: (tenantId: string, url: string) =>
    api.post<OfferingsExtractionResult>(
      `/api/v1/tenants/${tenantId}/offerings-extraction/extract-from-url`,
      { url },
    ),
  fromFile: async (tenantId: string, file: File): Promise<OfferingsExtractionResult> => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`/api/v1/tenants/${tenantId}/offerings-extraction/extract-from-file`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const text = await r.text();
    const json = text ? JSON.parse(text) : null;
    if (!r.ok) throw new ApiError(r.status, json ?? {});
    return json as OfferingsExtractionResult;
  },
  accept: (tenantId: string, offerings: OfferingProposal[]) =>
    api.post<{ ok: boolean; count: number }>(
      `/api/v1/tenants/${tenantId}/offerings-extraction/accept`,
      { offerings },
    ),
};

// ─── Provisioning ─────────────────────────────────────────────────────────

export interface ProvisionStepResult {
  step:
    | 'D1_CREATE'
    | 'MIGRATION'
    | 'MASTER_DATA_COPY'
    | 'OFFERINGS_SEED'
    | 'CUSTOM_DOMAIN'
    | 'ACTIVATE';
  status: 'completed' | 'skipped';
  details?: Record<string, unknown>;
}

export interface ProvisionResult {
  tenant: TenantFull;
  steps: ProvisionStepResult[];
}

export interface FirstClinicAdminArgs {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string | null;
  password?: string;
}

export interface FirstClinicAdminResult {
  userId: string;
  email: string;
  temporaryPassword: string;
  loginUrl: string;
  emailSent: boolean;
  emailError?: string;
}

export const tenantsApi = {
  list: (lifecycle?: LifecycleState) =>
    api.get<{ tenants: TenantSummary[] }>(
      lifecycle ? `/api/v1/tenants?lifecycle_state=${lifecycle}` : '/api/v1/tenants',
    ),
  get: (id: string) => api.get<{ tenant: TenantFull }>(`/api/v1/tenants/${id}`),
  create: (args: CreateTenantArgs) => api.post<{ tenant: TenantFull }>('/api/v1/tenants', args),
  update: (id: string, args: UpdateTenantArgs) =>
    api.put<{ tenant: TenantFull }>(`/api/v1/tenants/${id}`, args),
  suspend: (id: string, reason?: string) =>
    api.post<{ tenant: TenantFull }>(`/api/v1/tenants/${id}/suspend`, { reason }),
  reactivate: (id: string, reason?: string) =>
    api.post<{ tenant: TenantFull }>(`/api/v1/tenants/${id}/reactivate`, { reason }),
  archive: (id: string, reason?: string) =>
    api.post<{ tenant: TenantFull }>(`/api/v1/tenants/${id}/archive`, { reason }),
  offboard: (id: string, reason?: string) =>
    api.post<{ tenant: TenantFull }>(`/api/v1/tenants/${id}/offboard`, { reason }),
  delete: (id: string, reason?: string) => {
    // The api helper doesn't support a body on DELETE; use a POST workaround
    // OR send via fetch directly. Cleanest: use POST with a /delete suffix
    // (not what we did server-side) — for now a fetch-direct call.
    return fetch(`/api/v1/tenants/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }).then(async (r) => {
      const text = await r.text();
      const json = text ? JSON.parse(text) : null;
      if (!r.ok) throw new ApiError(r.status, json ?? {});
      return json as { tenant: TenantFull };
    });
  },
  provision: (id: string) =>
    api.post<ProvisionResult>(`/api/v1/tenants/${id}/provision`),
  createFirstClinicAdmin: (id: string, args: FirstClinicAdminArgs) =>
    api.post<FirstClinicAdminResult>(`/api/v1/tenants/${id}/first-clinic-admin`, args),
};
